/**
 * useCampaignReviewControls — write-side companion to useCampaignReviewDeepLink.
 *
 * The reader (useCampaignReviewDeepLink) maps URL → local `dialogCreatorId`;
 * this hook maps user actions → both `dialogCreatorId` AND URL in one call, so
 * a click writes a shareable link AND a reload reopens the panel.
 *
 * URL mapping mirrors the reader exactly so a written URL reloads identically:
 *   openReview(row)            → ?panel=<kind>&creator=<username>
 *   closeReview()              → clears panel/creator/sub/thread
 *   setThreadOpenUrl(true)     → ?thread=open
 *   setThreadOpenUrl(false)    → clears ?thread
 *
 * `<kind>` is 'script-review' or 'content-review' — picked by the tab.
 *
 * Keyless (shadow) creators have no username so they can't be URL-keyed: the
 * panel still opens locally (the setter still fires) but no `?creator=` param
 * is written. This matches useCampaignPanelControls' shadow-creator guard.
 *
 * No loop: opening sets `dialogCreatorId` immediately AND writes the URL. The
 * reader's effect runs, finds the same id, and calls the same setter — setState
 * is idempotent (same id = no extra render).
 */
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CampaignSearch } from './search';

export type ReviewKind = 'script-review' | 'content-review';

type ReviewRow = {
  id: string;
  platform?: { username?: string };
};

export function useCampaignReviewControls(
  kind: ReviewKind,
  setDialogCreatorId: (id: string | null) => void,
) {
  const navigate = useNavigate();

  /**
   * Open the script/content review panel for `row`.
   *
   * Sets local `dialogCreatorId` immediately (instant open) and writes
   * `?panel=<kind>&creator=<username>` so refresh restores the same panel.
   * Keyless creators open locally only.
   */
  const openReview = useCallback(
    (row: ReviewRow) => {
      setDialogCreatorId(row.id);

      const username = row.platform?.username?.trim();
      if (!username) return;

      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          panel: kind,
          creator: username,
          // sub belongs to insights; clear it so an existing ?sub doesn't leak.
          sub: undefined,
        })),
      } as never);
    },
    [kind, navigate, setDialogCreatorId],
  );

  /**
   * Close the panel.
   *
   * Clears local `dialogCreatorId` and the panel/creator/sub/thread URL params.
   * Preserves tab/pill/page so the user stays where they were on the list.
   */
  const closeReview = useCallback(() => {
    setDialogCreatorId(null);

    navigate({
      to: '.',
      search: ((p: Partial<CampaignSearch>) => ({
        ...p,
        panel: undefined,
        creator: undefined,
        sub: undefined,
        thread: undefined,
      })),
    } as never);
  }, [navigate, setDialogCreatorId]);

  /**
   * Mirror the panel's internal thread-open state to `?thread`.
   *
   * The panels keep their own `useState(threadOpen)`; this hook only writes the
   * URL so the toggle survives a refresh. Called from `onThreadOpenChange`.
   */
  const setThreadOpenUrl = useCallback(
    (open: boolean) => {
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          thread: open ? 'open' : undefined,
        })),
      } as never);
    },
    [navigate],
  );

  return { openReview, closeReview, setThreadOpenUrl };
}
